import { FormControl } from '../../../../../../widgets/formControl';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import React, { FC, useCallback } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ascComparator, stringFormat, T } from 'src/shared/helpers';
import GroupIcon from 'src/shared/material_icons/file/folder.svg';
import StepIcon from 'src/shared/material_icons/places/stairs.svg';
import styled from 'styled-components';
import RetargetIcon from 'src/shared/material_icons/maps/my_location.svg';
import { DuplicateIcon, EyeIcon, EyeOffIcon, LinkIcon, TrashIcon } from 'src/components/widgets/icons/scene';
import { SelectionCommand, SelectionCommands } from '../../scene-editors/selectionInspector';
import { ComposerAttributeMark, ComposerOptionMark } from '../../../configurator-composer/composer/components/composerAttributeTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerActionEditor, { composerActionTypeSections, createComposerAction } from './composerActionEditor';
import { DropdownActions } from '../../../../../../widgets/dropdown';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { ComposerActionTreeElement } from '../../../configurator-composer/composer/components/composerActionTreeElement';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import { applyComposerSharedActionEdit, getComposerSharedOptionActions } from '../../../configurator-composer/composer/helpers/composerBulkOptionActions';
import { useTreeNodeManager } from '../../tree';
import { PropertiesPanelDock, WORKSPACE_PANEL_CONTENT_MAX, WORKSPACE_PANEL_CONTENT_MIN } from '../../../configurator-scene-editor/useWorkspacePreferences';
import {
    ComposerDetailValue,
    ComposerEmptyHead,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerIconButton,
    composerInspectorTone,
    ComposerKindBadge,
    ComposerPanel,
    ComposerResizeHandle,
    ComposerSelectionBody,
    ComposerSection,
    ComposerSelectionHeader,
    composerPileKind,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabs,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import ComposerPreview from '../composerPreview';
import { ComposerCollapsibleColumn } from '../composerCollapsibleColumn';
import { useStructureSelectionCommands } from './structureSelectionCommands';
import { useOptionSelectionCommands } from './optionSelectionCommands';
import MergeIcon from 'src/shared/material_icons/maps/merge.svg';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import ReplaceInIcon from 'src/shared/material_icons/action/find_replace.svg';
import PreviewImageIcon from 'src/shared/material_icons/image/add_photo_alternate.svg';
import ComposerRetargetPanel from './composerRetargetPanel';
import { ComposerCameraSlot, ComposerChoice, ComposerObjectSlots } from '../composerSlots';
import { ComposerRevealButton } from '../composerReveal';
import ComposerActions from './composerActions';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerOptionActionNode,
    ComposerOptionActionsType
} from './composerAttributesModels';
import ComposerOptions from './composerOptions';
import ComposerPresetConfigurations from '../../../configurator-composer/composer/components/composerPresetConfigurations';
import CustomizableAreasEditor, { MultipleAreasPanel } from '../customizableAreasEditor';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import { ConfirmTwice, confirmLabel } from '../../confirmTwice';
import { ComposerSharedPrices } from './composerSharedPrices';

const EMPTY_OPTIONS: ComposerAttributeOptionNode[] = [];
const EMPTY_ACTIONS: ComposerOptionActionNode[] = [];

// UN INSPECTOR SOLO ACCANTO AL 3D (round 4, pacchetto H): opzioni e aree stanno nell'albero, e la
// colonna dei dettagli che si apriva fra la pagina dell'attributo e il 3D non c'è più. Sta accanto
// all'albero o in fondo a destra, come nello Scene Editor: lo decide Settings → Properties panel.
const WorkspaceGrid = styled.div<{ $propertiesPanel: PropertiesPanelDock; $detailsWidth?: number }>`
    display: grid;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    /* UNA RIGA SOLA, ALTA QUANTO LO SCHERMO (2026-10-15): le colonne stanno in un involucro che si chiude, e
       senza misura la riga cresceva quanto il contenuto dell'Inspector — tagliato in fondo invece di scorrere. */
    grid-template-rows: minmax(0, 1fr);
    grid-template-columns: ${(props) =>
        props.$propertiesPanel === 'side'
            ? `${props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(350px, var(--c2, 3fr))'} 6px minmax(0, var(--c3, 5fr))`
            : `minmax(0, var(--c3, 5fr)) 6px ${props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(350px, var(--c2, 3fr))'}`};
    overflow: hidden;
    background: var(--composer-app, #f0f0f0);

    /* L'INSPECTOR È IL PANNELLO DELLA GRIGLIA (l'altro figlio è il 3D): grigio, qualunque cosa mostri.
       Sta dentro la colonna che si chiude (ComposerCollapsibleColumn): la regola lo cerca anche lì. */
    > ${ComposerPanel}, [data-column-content] > ${ComposerPanel} {
        ${composerInspectorTone}
    }

    @media (max-width: 940px) {
        grid-template-columns: 1fr;

        > section:not(:last-child),
        > [role='separator'] {
            display: none;
        }

        > section:last-child,
        > :last-child {
            grid-area: 1 / 1;
        }
    }
`;

// I COMANDI DI UNA SELEZIONE MULTIPLA SONO QUELLI DELLO SCENE EDITOR (utente, 2026-10-14): lo stesso
// `OutlineButton` compatto, con l'icona accanto alla parola e la parola senza numero — quante righe
// hai preso lo dice già il badge in testata. Il tooltip di un comando spento lo avvolge in un div.
const BulkButtons = styled(SelectionCommands)`
    > div {
        width: 100%;
    }
`;

const SharedActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
`;

const SharedActionList = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 10px;
`;

interface BulkActionButtonProps extends React.ComponentProps<typeof SelectionCommand> {
    icon: React.ReactNode;
    disabledReason?: string;
}

const BulkActionButton: FC<BulkActionButtonProps> = ({ children, icon, disabled, disabledReason, ...buttonProps }) => {
    const button = (
        <SelectionCommand {...buttonProps} disabled={disabled}>
            {icon}
            <span>{children}</span>
        </SelectionCommand>
    );

    return disabled && disabledReason ? (
        <ZTooltip addContainer compact position='left' title={disabledReason}>
            {button}
        </ZTooltip>
    ) : (
        button
    );
};

const SubjectPanel: FC<{ subject?: ComposerAttributeNode; attributes: ComposerAttributeNode[] }> = ({ subject, attributes }) => {
    const [updateAttribute, validateAttributes, sceneObjects, sceneMaterials] = useComposerStore((store) => [
        store.updateAttribute,
        store.validateAttributes,
        store.sceneObjectList,
        store.sceneMaterialList
    ]);
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);

    if (!subject) {
        return (
            <ComposerPanel>
                <ComposerEmptyHead />
                <ComposerEmptyState>{T._('Pick a group, an attribute or an option in the tree.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const isGroup = subject.type === ComposerAttributeType.AttributeGroup;
    const typeLabel = isGroup ? T._('Group', 'Admin') : T._('Step', 'Admin');
    const childAttributes = attributes.filter((attribute) => attribute.parentID === subject.id);
    // Il Retarget del gruppo guarda tutto ciò che contiene, a ogni livello: gli attributi dei suoi step compresi.
    const containedAttributes: ComposerAttributeNode[] = [];
    const collect = (parentID: string) =>
        attributes
            .filter((attribute) => attribute.parentID === parentID)
            .forEach((attribute) => {
                if (attribute.type === ComposerAttributeType.Attribute) containedAttributes.push(attribute);
                else collect(attribute.id);
            });
    collect(subject.id);
    const containedActions = containedAttributes.flatMap((attribute) => attribute.options.flatMap((option) => option.actions));
    const cameraOptions =
        zkScene
            ?.get?.('cameraLocations')
            ?.models?.map((location) => ({ value: location.getLocationID(), label: location.get('name') || location.getLocationID() })) ?? [];
    const objectOptions = sceneObjects.filter((mesh) => sceneEditor?.isRealMesh(mesh)).map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const selectedObjects = (subject.objects || '').split(/[;,]/).filter(Boolean);
    const updateSubject = (changes: Partial<ComposerAttributeNode>) => updateAttribute({ ...subject, ...changes, isNew: true });
    const updateGroupImage = async (imageUrl: string, tempImageFileKey?: string) => {
        updateSubject({
            img: imageUrl,
            data: {
                ...subject.data,
                imageUrl,
                tempImageUrl: imageUrl,
                tempImageFileKey: tempImageFileKey ?? ''
            }
        });
        return imageUrl;
    };

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind={isGroup ? 'group' : 'neutral'}>
                    {subject.img ? <img src={subject.img} alt='' /> : isGroup ? <GroupIcon /> : <StepIcon />}
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={isGroup ? T._('Group name', 'Admin') : T._('Step name', 'Admin')}
                        placeholder={isGroup ? T._('Enter group name', 'Admin') : T._('Enter step name', 'Admin')}
                        isError={hasComposerValidationError(
                            subject,
                            isGroup ? ComposerValidationErrorCode.GROUP_NAME_REQUIRED : ComposerValidationErrorCode.STEP_NAME_REQUIRED
                        )}
                        value={subject.name}
                        onChange={(event) => updateSubject({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind={isGroup ? 'group' : 'neutral'}>{typeLabel}</ComposerKindBadge>
                        <span>
                            {stringFormat(
                                childAttributes.length === 1 ? T._('{0} attribute', 'Admin') : T._('{0} attributes', 'Admin'),
                                childAttributes.length.toString()
                            )}
                        </span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                <ComposerRevealButton id={subject.id} />
            </ComposerSubjectHeader>
            {/* Neither subject carries a constraint expression the product input can store, so the panel offers Settings alone.
                The shared face is left untouched, so the Links face an attribute was on survives a detour through a group. */}
            {/* RETARGET, POI SETTINGS, come le altre tab dei due editor (prototipo, utente 2026-10-20); si apre sulle Settings. */}
            <ComposerTabs defaultIndex={1}>
                <TabList>
                    <Tab>{T._('Retarget', 'Admin')}</Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerRetargetPanel
                        actions={containedActions}
                        objects={sceneObjects}
                        materials={sceneMaterials}
                        subjectLabel={subject.name}
                        onApply={(remapped) =>
                            containedAttributes.forEach((attribute) =>
                                updateAttribute({
                                    ...attribute,
                                    options: attribute.options.map((option) => ({
                                        ...option,
                                        actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                                    }))
                                })
                            )
                        }
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            {isGroup && (
                                <ComposerSection id='group.appearance' title={T._('Appearance', 'Admin')}>
                                    <FormControl
                                        label={T._('Group image', 'Admin')}
                                        tooltipLabelHtml={T._('The image displayed for this group in the configurator.', 'Admin')}
                                    >
                                        <ComposerUploadComponent passedImgUrl={subject.img} onImagePick={updateGroupImage} />
                                    </FormControl>
                                    <FormControl
                                        label={T._('Attributes direction', 'Admin')}
                                        tooltipLabelHtml={T._(
                                            'Choose how attributes are displayed in the configurator: in a row or column. This does not affect mobile devices.',
                                            'Admin'
                                        )}
                                    >
                                        <ComposerChoice
                                            ariaLabel={T._('Attributes direction', 'Admin')}
                                            options={[
                                                { value: MPlaza.AttributeGroupDirection.Horizontal as number, label: T._('Horizontal', 'Admin') },
                                                { value: MPlaza.AttributeGroupDirection.Vertical as number, label: T._('Vertical', 'Admin') }
                                            ]}
                                            value={
                                                subject.direction === MPlaza.AttributeGroupDirection.Vertical
                                                    ? (MPlaza.AttributeGroupDirection.Vertical as number)
                                                    : (MPlaza.AttributeGroupDirection.Horizontal as number)
                                            }
                                            onChange={(direction) => updateSubject({ direction })}
                                        />
                                    </FormControl>
                                    {subject.direction === MPlaza.AttributeGroupDirection.Vertical && (
                                        <FormControl
                                            label={T._('Attributes state', 'Admin')}
                                            tooltipLabelHtml={T._(
                                                'Choose how attributes are opened in the configurator. This does not affect mobile devices.',
                                                'Admin'
                                            )}
                                        >
                                            <ComposerChoice
                                                ariaLabel={T._('Attributes state', 'Admin')}
                                                options={[
                                                    { value: 'accordion', label: T._('First open, others with accordion', 'Admin') },
                                                    { value: 'all-open', label: T._('All attributes opened', 'Admin') }
                                                ]}
                                                value={subject.attributesAlwaysOpened ? 'all-open' : 'accordion'}
                                                onChange={(state) => updateSubject({ attributesAlwaysOpened: state === 'all-open' })}
                                            />
                                        </FormControl>
                                    )}
                                </ComposerSection>
                            )}
                            <ComposerSection id='group.camera' title={T._('Camera', 'Admin')}>
                                <FormControl
                                    label={T._('Camera position', 'Admin')}
                                    tooltipLabelHtml={
                                        isGroup
                                            ? T._('The camera moves to this position when the customer selects this group in the configurator.', 'Admin')
                                            : T._('The camera moves to this position when the customer opens this step in the configurator.', 'Admin')
                                    }
                                >
                                    <ComposerCameraSlot
                                        id={`composer-group-${subject.id}-camera`}
                                        value={subject.camera}
                                        options={cameraOptions}
                                        onChange={(camera) => updateSubject({ camera })}
                                    />
                                </FormControl>
                                {isGroup && (
                                    <FormControl
                                        label={T._('Objects', 'Admin')}
                                        tooltipLabelHtml={T._('When you click on these objects the camera will move to the selected position', 'Admin')}
                                    >
                                        <ComposerObjectSlots
                                            id={`composer-group-${subject.id}-objects`}
                                            value={selectedObjects}
                                            options={objectOptions}
                                            onChange={(ids) => updateSubject({ objects: ids.join(';') })}
                                        />
                                    </FormControl>
                                )}
                            </ComposerSection>
                            <ComposerSection id='group.attributes' title={T._('Attributes', 'Admin')}>
                                <ComposerDetailValue>
                                    {childAttributes.map((attribute) => attribute.name).join(', ') || T._('No attributes assigned', 'Admin')}
                                </ComposerDetailValue>
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

const MultipleAttributesPanel: FC<{ attributes: ComposerAttributeNode[] }> = ({ attributes }) => {
    const {
        selectionRules,
        selectionOptions,
        duplicateSelection,
        setSelectionEnabled,
        manageSelectedAttributeLinks,
        removeSelection,
        retargetSelection,
        groupSelection,
        mergeSelection
    } = useStructureSelectionCommands(attributes);
    const { openImagePicker, picker } = useOptionSelectionCommands();
    const [selectAttributes, selectOptions] = useComposerStore((store) => [store.selectAttributes, store.selectOptions]);

    return (
        <ComposerPanel>
            <ComposerSelectionHeader
                kind={composerPileKind(
                    attributes.map((attribute) =>
                        attribute.type === ComposerAttributeType.AttributeGroup || attribute.type === ComposerAttributeType.AttributeStep
                            ? 'group'
                            : attribute.type === ComposerAttributeType.Attribute
                              ? 'attribute'
                              : 'neutral'
                    )
                )}
                entries={attributes.map((attribute) => ({ id: attribute.id, name: attribute.name, icon: <ComposerAttributeMark attribute={attribute} /> }))}
                onPick={(id) => {
                    const picked = attributes.find((attribute) => attribute.id === id);
                    if (!picked) return;
                    selectAttributes([picked]);
                    selectOptions(null);
                }}
            />
            <ComposerSelectionBody>
                <BulkButtons>
                    <BulkActionButton
                        disabled={!selectionRules.canEnable}
                        disabledReason={T._('All selected rows are already enabled.', 'Admin')}
                        icon={<EyeIcon />}
                        onClick={() => setSelectionEnabled(true)}
                    >
                        {T._('Enable', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canDisable}
                        disabledReason={T._('All selected rows are already disabled.', 'Admin')}
                        icon={<EyeOffIcon />}
                        onClick={() => setSelectionEnabled(false)}
                    >
                        {T._('Disable', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canManageLinks}
                        disabledReason={T._('Select only attributes.', 'Admin')}
                        icon={<LinkIcon />}
                        onClick={manageSelectedAttributeLinks}
                    >
                        {T._('Manage links…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canRetarget}
                        disabledReason={T._('Select only groups and attributes.', 'Admin')}
                        icon={<RetargetIcon />}
                        onClick={retargetSelection}
                    >
                        {T._('Retarget…', 'Admin')}
                    </BulkActionButton>
                    {/* GROUP, MERGE E L'IMMAGINE STANNO SULLA SELEZIONE (utente, 2026-10-15): prima erano tasti nella barra
                        dell'albero, con una finestra che chiedeva di nuovo quali righe. */}
                    <BulkActionButton
                        disabled={!selectionRules.canGroup}
                        disabledReason={T._('A group cannot go inside another group.', 'Admin')}
                        icon={<GroupIcon />}
                        onClick={groupSelection}
                    >
                        {T._('Group', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canMerge}
                        disabledReason={T._('Select only attributes with options.', 'Admin')}
                        icon={<MergeIcon />}
                        onClick={mergeSelection}
                    >
                        {T._('Merge', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canImage}
                        disabledReason={T._('The selected rows have no options.', 'Admin')}
                        icon={<PreviewImageIcon />}
                        onClick={(event) => openImagePicker(event.currentTarget, selectionOptions)}
                    >
                        {T._('Preview image…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canDuplicate}
                        disabledReason={T._('Select only groups, steps or attributes but not mixed.', 'Admin')}
                        icon={<DuplicateIcon />}
                        onClick={duplicateSelection}
                    >
                        {T._('Clone', 'Admin')}
                    </BulkActionButton>
                    <ConfirmTwice onConfirm={removeSelection}>
                        {({ armed, ...confirm }) => (
                            <BulkActionButton $danger icon={<TrashIcon />} {...confirm}>
                                {armed ? confirmLabel() : T._('Remove', 'Admin')}
                            </BulkActionButton>
                        )}
                    </ConfirmTwice>
                </BulkButtons>
            </ComposerSelectionBody>
            {picker}
        </ComposerPanel>
    );
};

const MultipleOptionsPanel: FC<{
    selectedAttribute: ComposerAttributeNode;
    options: ComposerAttributeOptionNode[];
    materials: BABYLON.Material[];
    meshes: BABYLON.AbstractMesh[];
    cameras: BABYLON.Camera[];
}> = ({ selectedAttribute, options, materials, meshes, cameras }) => {
    const [updateOption, updateAction, removeAction, selectOptions] = useComposerStore((store) => [
        store.updateOption,
        store.updateAction,
        store.removeAction,
        store.selectOptions
    ]);
    const sharedActions = React.useMemo(() => getComposerSharedOptionActions(options), [options]);
    const { cloneOptions, removeOptions, openCopyPicker, openImagePicker, picker } = useOptionSelectionCommands();

    const cloneSelection = () => cloneOptions(options);

    const setSelectionEnabled = (enabled: boolean) => {
        options.forEach((option) => updateOption({ ...option, enabled }));
    };

    const removeSelection = () => removeOptions(options);

    // LE AZIONI CONDIVISE SI MODIFICANO NELLA LORO CARD, come quelle di un'opzione sola (utente, 2026-10-15):
    // prima «Edit for N options…» apriva la finestra dell'azione. Ogni riga scrive subito su tutte le opzioni
    // scelte; la card resta aperta perché la chiave è l'azione della prima opzione, che non cambia.
    const [openSharedIds, setOpenSharedIds] = React.useState<string[]>([]);
    const toggleShared = (id: string) =>
        setOpenSharedIds((current) => (current.includes(id) ? current.filter((candidate) => candidate !== id) : [...current, id]));
    const printTypes = useComposerStore((store) => store.printTypes);

    // ADD ACTION TO THE SELECTED OPTIONS (utente, 2026-10-20): la stessa azione, vuota, in ogni opzione scelta. È un
    // dato delle opzioni, salvato col prodotto come quelle aggiunte una a una. Nasce come card condivisa, aperta: lì si
    // scelgono oggetti e valore per tutte insieme.
    const addSharedAction = (type: ComposerOptionActionsType) => {
        const created = options.map((option) => {
            const action = createComposerAction(option.attributeID, option.id, type, option.actions.length);
            updateAction(action);
            return action;
        });
        if (created[0]) setOpenSharedIds((current) => [...current, created[0].id]);
    };

    return (
        <ComposerPanel>
            <ComposerSelectionHeader
                kind='option'
                entries={options.map((option) => ({ id: option.id, name: option.name, icon: <ComposerOptionMark /> }))}
                onPick={(id) => {
                    const picked = options.find((option) => option.id === id);
                    if (picked) selectOptions([picked]);
                }}
            />
            <ComposerSelectionBody>
                <SharedActions>
                    <ComposerSection
                        id='options.shared-actions'
                        title={T._('Shared actions', 'Admin')}
                        trailing={
                            <DropdownActions
                                header={
                                    <ZTooltip title={T._('Add an action to the selected options', 'Admin')}>
                                        <ComposerIconButton aria-label={T._('Add an action to the selected options', 'Admin')}>
                                            <PlusIcon />
                                        </ComposerIconButton>
                                    </ZTooltip>
                                }
                                sections={composerActionTypeSections(addSharedAction)}
                                minWidth='236px'
                                orientation='RIGHT'
                            />
                        }
                    >
                        {sharedActions.length === 0 ? (
                            <ComposerUnsupportedNote>
                                {T._(
                                    'The selected options do not have an action that can be matched safely. An action must occur exactly once in every selected option.',
                                    'Admin'
                                )}
                            </ComposerUnsupportedNote>
                        ) : (
                            <>
                                <SharedActionList>
                                    {sharedActions.map((sharedAction) => {
                                        const id = sharedAction.representative.id;
                                        return (
                                            <ComposerActionTreeElement
                                                key={id}
                                                action={sharedAction.representative}
                                                open={openSharedIds.includes(id)}
                                                onToggleOpen={() => toggleShared(id)}
                                                // CI SI RIPENSA (utente, 2026-10-20): Remove toglie l'azione da tutte le opzioni scelte, con la
                                                // doppia conferma di ogni Remove.
                                                onRemove={() => {
                                                    sharedAction.actions.forEach((action) => removeAction(action.attributeID, action.optionID, action.id));
                                                    setOpenSharedIds((current) => current.filter((candidate) => candidate !== id));
                                                }}
                                                caption={sharedAction.hasMixedValues ? T._('Mixed values', 'Admin') : undefined}
                                                findMesh={(meshID) => meshes.find((mesh) => mesh.id === meshID || mesh.name === meshID)}
                                                findMaterial={(materialID) =>
                                                    materials.find((material) => material.id === materialID || material.name === materialID)
                                                }
                                                findCamera={(cameraID) => cameras.find((camera) => camera.id === cameraID || camera.name === cameraID)}
                                                findPrintingMethod={(methodID) => {
                                                    const method = printTypes.find((candidate) => String(candidate.printTypeID) === methodID);
                                                    return method ? { name: method.name || methodID } : undefined;
                                                }}
                                                editor={
                                                    <ComposerActionEditor
                                                        action={sharedAction.representative}
                                                        materials={materials}
                                                        meshes={meshes}
                                                        cameras={cameras}
                                                        onChange={(edited) => applyComposerSharedActionEdit(sharedAction.actions, edited).forEach(updateAction)}
                                                    />
                                                }
                                            />
                                        );
                                    })}
                                </SharedActionList>
                                <ComposerUnsupportedNote>
                                    {T._('Only actions present exactly once in every selected option can be edited together.', 'Admin')}
                                </ComposerUnsupportedNote>
                            </>
                        )}
                    </ComposerSection>
                    <ComposerSection id='options.shared-prices' title={T._('Shared prices', 'Admin')}>
                        <ComposerSharedPrices options={options} />
                    </ComposerSection>
                </SharedActions>
                <BulkButtons>
                    <BulkActionButton
                        disabled={options.every((option) => option.enabled !== false)}
                        disabledReason={T._('All selected options are already enabled.', 'Admin')}
                        icon={<EyeIcon />}
                        onClick={() => setSelectionEnabled(true)}
                    >
                        {T._('Enable', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={options.every((option) => option.enabled === false)}
                        disabledReason={T._('All selected options are already disabled.', 'Admin')}
                        icon={<EyeOffIcon />}
                        onClick={() => setSelectionEnabled(false)}
                    >
                        {T._('Disable', 'Admin')}
                    </BulkActionButton>
                    {/* COPIA E IMMAGINE SULLE OPZIONI SCELTE (utente, 2026-10-15): l'elenco degli attributi o delle fonti
                        si apre sotto il tasto, invece della finestra della barra dell'albero. */}
                    <BulkActionButton icon={<CopyToIcon />} onClick={(event) => openCopyPicker(event.currentTarget, options, 'copy')}>
                        {T._('Copy to…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton icon={<ReplaceInIcon />} onClick={(event) => openCopyPicker(event.currentTarget, options, 'replace')}>
                        {T._('Replace in…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton icon={<PreviewImageIcon />} onClick={(event) => openImagePicker(event.currentTarget, options)}>
                        {T._('Preview image…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton icon={<DuplicateIcon />} onClick={cloneSelection}>
                        {T._('Clone', 'Admin')}
                    </BulkActionButton>
                    <ConfirmTwice onConfirm={removeSelection}>
                        {({ armed, ...confirm }) => (
                            <BulkActionButton $danger icon={<TrashIcon />} {...confirm}>
                                {armed ? confirmLabel() : T._('Remove', 'Admin')}
                            </BulkActionButton>
                        )}
                    </ConfirmTwice>
                </BulkButtons>
            </ComposerSelectionBody>
            {picker}
        </ComposerPanel>
    );
};

// L'INSPECTOR PUÒ STARE DALLE DUE PARTI DEL 3D (Settings → Properties panel): il bordo misura il
// pannello dalla sua parte, e a destra trascinare verso sinistra lo allarga.
const ResizeBoundary: FC<{
    label: string;
    dock: PropertiesPanelDock;
    onResize: (width: number) => void;
    /** Al rilascio: la misura scelta diventa la preferenza, una volta sola e non a ogni pixel. */
    onCommit: (width: number) => void;
    onReset: () => void;
}> = ({ label, dock, onResize, onCommit, onReset }) => {
    const panelOf = (handle: HTMLElement) => (dock === 'side' ? handle.previousElementSibling : handle.nextElementSibling) as HTMLElement | null;
    const direction = dock === 'side' ? 1 : -1;
    // The same floor and ceiling the Scene Editor columns are held to.
    const clampWidth = (width: number) => Math.min(Math.max(width, WORKSPACE_PANEL_CONTENT_MIN), WORKSPACE_PANEL_CONTENT_MAX);
    const startResize = (event: React.MouseEvent<HTMLDivElement>) => {
        const panel = panelOf(event.currentTarget);
        if (!panel) return;
        const startX = event.clientX;
        const startWidth = panel.getBoundingClientRect().width;
        let nextWidth = startWidth;
        const move = (moveEvent: MouseEvent) => {
            nextWidth = clampWidth(startWidth + direction * (moveEvent.clientX - startX));
            onResize(nextWidth);
        };
        const stop = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
            onCommit(nextWidth);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    };

    return (
        <ZTooltip title={T._('Drag to resize · double-click to reset', 'Admin')} position='left'>
            <ComposerResizeHandle
                aria-label={label}
                aria-orientation='vertical'
                onDoubleClick={onReset}
                onKeyDown={(event) => {
                    const panel = panelOf(event.currentTarget);
                    if (!panel || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
                    event.preventDefault();
                    const nextWidth = clampWidth(panel.getBoundingClientRect().width + direction * (event.key === 'ArrowRight' ? 16 : -16));
                    onResize(nextWidth);
                    onCommit(nextWidth);
                }}
                onMouseDown={startResize}
                role='separator'
                tabIndex={0}
            />
        </ZTooltip>
    );
};

// L'INSPECTOR NASCE LARGO COME QUELLO DELLO SCENE EDITOR (utente, 2026-10-15): la misura è la stessa
// preferenza, `sizes.right`, e trascinarne il bordo la riscrive per tutte e due le stanze. Durante il
// trascinamento la misura vive qui; al rilascio diventa la preferenza.
const ComposerAttributeContentPage: FC<{
    propertiesPanel: PropertiesPanelDock;
    /** `preferences.sizes.right`: la larghezza dell'Inspector, la stessa dello Scene Editor. */
    inspectorWidth?: number;
    onInspectorResize?: (width: number) => void;
    onInspectorReset?: () => void;
    /** `preferences.collapsed` contiene `right`: la colonna è chiusa, larga quanto il rail. */
    inspectorCollapsed?: boolean;
    onToggleInspector?: () => void;
    /** La larghezza del rail della scena: da chiusa, la colonna è larga così. */
    railWidth: number;
    onOpenFullEditor?: (area: ComposerAreaNode, templateNodeID: string) => Promise<boolean | void>;
    onPreparePresetEditor: (emptyPresetID?: string) => Promise<boolean>;
}> = ({
    propertiesPanel,
    inspectorWidth,
    onInspectorResize,
    onInspectorReset,
    inspectorCollapsed = false,
    onToggleInspector,
    railWidth,
    onOpenFullEditor,
    onPreparePresetEditor
}) => {
    const [attributes, selectedAttributes, selectedOptions, storeMeshList, storeMaterialsList, storeCameraList] = useComposerStore((store) => [
        store.attributes,
        store.selectedAttributes,
        store.selectedOptions,
        store.sceneObjectList,
        store.sceneMaterialList,
        store.sceneCameraList
    ]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const selectedAreas = useComposerStore((store) => store.selectedAreas);
    const [detailsWidth, setDetailsWidth] = React.useState<number>();
    const selectedAttribute = selectedAttributes?.[0] ?? null;
    const actions = selectedOptions?.length === 1 ? selectedOptions[0].actions : EMPTY_ACTIONS;
    const multipleAttributes = (selectedAttributes?.length ?? 0) > 1;
    const multipleOptions = (selectedOptions?.length ?? 0) > 1;
    const createActionOverlay = useCallback(() => () => null, []);
    const actionsTreeState = useTreeNodeManager<ComposerOptionActionNode>(actions, createActionOverlay);
    const meshes = storeMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh)).sort(ascComparator((mesh) => mesh.name ?? ''));
    const materials = storeMaterialsList
        .filter((material) => material.id !== 'default material' && material.name !== 'default material')
        .sort(ascComparator((material) => material.name ?? ''));
    const cameras = storeCameraList
        .filter((camera) => camera.id !== 'default camera' && camera.name !== 'default camera')
        .sort(ascComparator((camera) => camera.name ?? ''));

    // L'INSPECTOR MOSTRA LA COSA PIÙ PICCOLA CHE HAI SCELTO: un'opzione (o più), poi un'area, poi
    // l'attributo, il gruppo o il mucchio della struttura. L'albero sceglie, qui si legge.
    const inspectorPanel =
        multipleOptions && selectedAttribute ? (
            <MultipleOptionsPanel
                selectedAttribute={selectedAttribute}
                options={selectedOptions ?? []}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
            />
        ) : selectedOptions?.length === 1 && selectedAttribute ? (
            <ComposerActions
                selectedAttribute={selectedAttribute}
                selectedOption={selectedOptions[0]}
                actions={actions}
                multiple={false}
                state={actionsTreeState}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
            />
        ) : (selectedAreas?.length ?? 0) > 1 ? (
            <MultipleAreasPanel areas={selectedAreas ?? []} />
        ) : multipleAttributes ? (
            <MultipleAttributesPanel attributes={selectedAttributes ?? []} />
        ) : selectedAttribute?.type === ComposerAttributeType.PreSetContainer ? (
            <ComposerPresetConfigurations key={selectedAttribute.id} attribute={selectedAttribute} onPrepareEditor={onPreparePresetEditor} />
        ) : selectedAttribute?.type === ComposerAttributeType.Attribute ? (
            selectedAttribute.attributeType === MPlaza.AttributeType.CustomizableArea ? (
                <CustomizableAreasEditor
                    key={selectedAreas?.[0]?.id ?? selectedAttribute.id}
                    selectedAttribute={selectedAttribute}
                    onOpenFullEditor={onOpenFullEditor}
                />
            ) : (
                <ComposerOptions selectedAttribute={selectedAttribute} options={selectedAttribute.options ?? EMPTY_OPTIONS} multiple={false} />
            )
        ) : (
            <SubjectPanel subject={selectedAttribute ?? undefined} attributes={attributes} />
        );

    // Chiusa, la colonna non ha un confine da tirare: resta la cucitura, vuota, come nella scena.
    const resizeInspector = inspectorCollapsed ? (
        <div aria-hidden='true' />
    ) : (
        <ResizeBoundary
            label={T._('Resize properties panel', 'Admin')}
            dock={propertiesPanel}
            onResize={setDetailsWidth}
            onCommit={(width) => {
                setDetailsWidth(undefined);
                onInspectorResize?.(width);
            }}
            onReset={() => {
                setDetailsWidth(undefined);
                onInspectorReset?.();
            }}
        />
    );

    // IL PIEDE DELL'INSPECTOR, come nella scena: il chevron sta dalla parte da cui la colonna si chiude —
    // verso l'albero quando gli sta accanto, verso il bordo quando sta a destra.
    const inspectorColumn = (
        <ComposerCollapsibleColumn
            collapsed={inspectorCollapsed}
            side={propertiesPanel === 'side' ? 'left' : 'right'}
            railWidth={railWidth}
            foot='band'
            onToggle={() => onToggleInspector?.()}
        >
            {inspectorPanel}
        </ComposerCollapsibleColumn>
    );

    return (
        <WorkspaceGrid $propertiesPanel={propertiesPanel} $detailsWidth={inspectorCollapsed ? railWidth : (detailsWidth ?? inspectorWidth)}>
            {propertiesPanel === 'side' ? (
                <>
                    {inspectorColumn}
                    {resizeInspector}
                    <ComposerPreview />
                </>
            ) : (
                <>
                    <ComposerPreview />
                    {resizeInspector}
                    {inspectorColumn}
                </>
            )}
        </WorkspaceGrid>
    );
};

export default ComposerAttributeContentPage;
